import { useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import { formatMutationResource } from 'utils/misc';
import type { IntersectingUnit, WellboreMapData } from 'types/api-responses';
import {
  afeCatalogWellIntersectionMutationAtom,
  afeCatalogWellMapMutationAtom,
} from './map-atoms';

export interface AfeWellGeometry {
  wellbores: WellboreMapData[];
  intersectingUnits: IntersectingUnit[];
  isLoading: boolean;
}

/**
 * Fetches wellbore + intersecting-unit geometry for a selected AFE's UWI
 * via the shared mutation-atom pattern. Both POSTs fire in parallel when
 * `uwi` changes; returns empty arrays when `uwi` is null.
 *
 * @param {string | null} uwi - The UWI of the selected AFE, or null to clear geometry.
 * @return {AfeWellGeometry} Wellbore + intersecting-unit geometry and loading state.
 */
export function useAfeWellGeometry(uwi: string | null): AfeWellGeometry {
  const [wellMapState, setWellMap] = useAtom(afeCatalogWellMapMutationAtom);
  const [intersectState, setIntersect] = useAtom(
    afeCatalogWellIntersectionMutationAtom
  );

  const wellMap = formatMutationResource(wellMapState);
  const intersect = formatMutationResource(intersectState);

  useEffect(() => {
    if (!uwi) return;
    setWellMap({
      url: '/queries/wells',
      type: 'POST',
      data: { uwi, includeDeviationSurvey2d: true },
    });
    setIntersect({
      url: '/queries/well_intersection_analysis',
      type: 'POST',
      data: { query: { well_uwi: uwi } },
    });
  }, [uwi, setWellMap, setIntersect]);

  const wellbores = useMemo<WellboreMapData[]>(
    () => (uwi ? (wellMap.data?.results?.[0]?.wellBores ?? []) : []),
    [uwi, wellMap.data]
  );
  const intersectingUnits = useMemo<IntersectingUnit[]>(
    () => (uwi ? (intersect.data?.intersectingUnits ?? []) : []),
    [uwi, intersect.data]
  );

  return {
    wellbores,
    intersectingUnits,
    isLoading: wellMap.loading || intersect.loading,
  };
}
